/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import React from 'react';
import { connect, useDispatch } from 'react-redux';
import get from 'lodash/get';
import forEach from 'lodash/forEach';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';

import {
    Select,
    FlexRow,
    FlexCol,
    Heading,
    announce,
    Alert,
    useFlags,
} from 'sarsaparilla';
import { paginator } from 'shared-ui/utils/paginator';
import { Footer } from 'site-kit';

import Card from '../components/SearchResultCard/Card';
import { CardSurfaceContext } from '../components/SearchResultCard/CardSurfaceContext';
import CardLoading from '../components/SearchResultCard/CardLoading';
import * as searchActions from '../actions/search';
import * as searchMapSyncActions from '../actions/searchMapSync';
import SearchPagination from '../components/SearchPagination';
import SearchPaginationText from '../components/SearchPaginationText';
import SearchFilters from './SearchFilters';
import { SearchFiltersDialog } from '../ui/SearchFiltersDialog/SearchFiltersDialog';
import SearchCriteriaInputs from './SearchCriteriaInputs';
import SearchFilterPills from '../components/SearchFilterPills';
import { AiSearchPrompt } from '../components/aiSearch/AiSearchPrompt';
import * as types from '../constants';
import { useMapControl } from '../context/MapControlContext';
import { setVisibleMapItems } from '../actions/searchMapSync';

import { A250SearchPromoWrapper } from '../components/America250Promo/A250Promo';
import { NoSearchResultsMessage } from '../components/NoSearchResultsMessage';

class SearchResultsList extends React.Component {
    static propTypes = {
        clickedMapItemId: PropTypes.string,
        screenWidth: PropTypes.number,
        dispatch: PropTypes.func.isRequired,
        search: PropTypes.object,
        searchResults: PropTypes.object,
        isCampingSearch: PropTypes.bool,
        flags: PropTypes.object,
        searchAggregations: PropTypes.any,
        visibleMapItemIds: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.string),
            PropTypes.oneOf([null]),
        ]),
        fitBoundsToMarkers: PropTypes.func,
        resetVisibleMapItems: PropTypes.func,
        useNewSearchFiltersDialog: PropTypes.bool,
        isFiltersDialogOpen: PropTypes.bool,
        onFiltersDialogOpenChange: PropTypes.func,
    };

    static sortOptionsDefault = [
        {
            label: 'Best Match',
            value: 'score',
        },
        {
            label: 'Distance',
            value: 'distance',
        },
        {
            label: 'Rating',
            value: 'average_rating',
        },
        {
            label: 'Price',
            value: 'price asc',
        },
        {
            label: 'Mobile Coverage',
            value: 'aggregate_cell_coverage',
        },
    ];

    static sortOptionsAvailability = [
        {
            label: 'Best Match',
            value: 'score',
        },
        {
            label: 'Available',
            value: 'available',
        },
        {
            label: 'Distance',
            value: 'distance',
        },
        {
            label: 'Rating',
            value: 'average_rating',
        },
        {
            label: 'Price',
            value: 'price asc',
        },
        {
            label: 'Mobile Coverage',
            value: 'aggregate_cell_coverage',
        },
    ];

    static displayName = 'SearchResultsList';

    constructor(props) {
        super(props);

        this.handleSortChange = this.handleSortChange.bind(this);
        this.onCardLeaveEvent = this.onCardLeaveEvent.bind(this);
        this.onCardOverEvent = this.onCardOverEvent.bind(this);
        this.onPage = this.onPage.bind(this);
        this.onFilteredPage = this.onFilteredPage.bind(this);
        this.onApplyFilters = this.onApplyFilters.bind(this);

        this.state = {
            filteredStart: 0,
        };

        this.cardListDivRef = React.createRef(null);
    }

    componentDidUpdate(prevProps) {
        // Reset filtered pagination when visible map items change
        if (this.props.visibleMapItemIds !== prevProps.visibleMapItemIds) {
            this.setState({ filteredStart: 0 });
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            nextProps.clickedMapItemId &&
            nextProps.clickedMapItemId !== this.props.clickedMapItemId
        ) {
            const searchResults = this.props.searchResults;
            if (!searchResults?.results?.length) return;
            let card;
            let entityType;
            let inPage;

            for (let i = 0; i < searchResults.results.length; i += 1) {
                const searchResult = searchResults.results[i];

                // TODO: Should we also check for entity_type???
                if (searchResult.entity_id === nextProps.clickedMapItemId) {
                    let pageSize = this.props.search.size;

                    if (!pageSize) {
                        pageSize = 20;
                    }

                    entityType = searchResult.entity_type;
                    inPage = Math.ceil((i + 1) / pageSize);
                    break;
                }
            }

            card = document.getElementById(
                `rec-card-${nextProps.clickedMapItemId}_${entityType}`
            );

            if (card) {
                card.scrollIntoView({
                    behavior: 'smooth',
                });
                setTimeout(() => {
                    card.classList.toggle('rec-list-item-highlight');
                }, 500);
                setTimeout(() => {
                    card.classList.toggle('rec-list-item-highlight');
                }, 1000);
            } else {
                this.onPage(inPage);

                const interval = window.setInterval(() => {
                    card = document.getElementById(
                        `rec-card-${nextProps.clickedMapItemId}_${entityType}`
                    );

                    if (card) {
                        window.clearInterval(interval);
                        card.scrollIntoView({
                            behavior: 'smooth',
                        });
                        setTimeout(() => {
                            card.classList.toggle('rec-list-item-highlight');
                        }, 500);
                        setTimeout(() => {
                            card.classList.toggle('rec-list-item-highlight');
                        }, 1000);
                    }
                }, 100);
            }
        }
    }

    onCardLeaveEvent() {
        this.props.dispatch(searchMapSyncActions.handleDeselectCard());
    }

    onCardOverEvent(item) {
        this.props.dispatch(searchMapSyncActions.handleSelectCard(item));
    }

    onApplyFilters = () => {
        this.onPage(1);
        setTimeout(() => {
            this.props.dispatch(searchActions.navigateToSearchResults());
        }, 200);
    };

    // The filters dialog only applies once its change is in the store, so it can
    // search right away instead of waiting like onApplyFilters
    onApplyDialogFilters = () => {
        this.onPage(1);
        this.props.dispatch(searchActions.navigateToSearchResults());
    };

    onPage(page) {
        let pageSize = this.props.search.size;

        if (!pageSize) {
            pageSize = 20;
        }

        const newStart = (page - 1) * pageSize;

        this.props.dispatch(
            searchActions.updateSearchCriterias({
                start: newStart,
            })
        );

        // Update the 'start' URL search param
        const url = new URL(window.location.href);
        url.searchParams.set('start', newStart);
        window.history.replaceState(window.history.state, '', url.toString());

        // Reset scroll position
        window.scrollTo(0, 0);
        if (this.cardListDivRef.current) {
            // Reset scroll position of the card list div
            this.cardListDivRef.current.scrollTo(0, 0);
        }
    }

    onFilteredPage(page) {
        let pageSize = this.props.search.size;

        if (!pageSize) {
            pageSize = 20;
        }

        const newStart = (page - 1) * pageSize;
        this.setState({ filteredStart: newStart });

        // Reset scroll position
        window.scrollTo(0, 0);
        if (this.cardListDivRef.current) {
            this.cardListDivRef.current.scrollTo(0, 0);
        }
    }

    getCampsiteTypeFilterValues() {
        const campsiteTypes = get(this.props.search, 'uiFilters.campsiteTypes', {});
        const hasElectricity = !isEmpty(
            get(
                this.props.search,
                'uiFilters.campsiteAmenities.campsite-amenities-electricity-hookup',
                {}
            )
        );
        let siteTypes = [];
        forEach(campsiteTypes, (campsiteTypeObject) => {
            const campsiteFilters = get(campsiteTypeObject, 'filters', []);
            forEach(campsiteFilters, (f) => {
                let filters = [];
                if (f.filterValue && f.filterValue !== '') {
                    filters = f.filterValue.split(',');
                }
                if (!isEmpty(filters)) {
                    siteTypes.push(...filters);
                }
            });
        });

        if (hasElectricity) {
            siteTypes = filter(siteTypes, (s) => {
                return !s.includes('NONELECTRIC');
            });
        }
        return siteTypes;
    }

    handleSortChange(value) {
        let announceSelection = null;
        switch (value) {
            case 'available':
                announceSelection = 'availability';
                break;
            case 'distance':
                announceSelection = 'distance';
                break;
            case 'price asc':
                announceSelection = 'price low to high';
                break;
            case 'price desc':
                announceSelection = 'price high to low';
                break;
            case 'score':
            default:
                announceSelection = 'best match';
                break;
        }
        if (value === 'distance') {
            this.props.dispatch(
                searchActions.updateSearchCriterias({
                    start: null,
                    cursor_mark: null,
                    sort: value,
                    lat: this.props.search.map_center_lat,
                    lng: this.props.search.map_center_lng,
                })
            );
            announce(`The search results are now sorted by ${announceSelection}.`);
        } else {
            this.props.dispatch(
                searchActions.updateSearchCriterias({
                    start: null,
                    cursor_mark: null,
                    sort: value,
                })
            );
            announce(`The search results are now sorted by ${announceSelection}.`);
        }
        setTimeout(() => {
            this.props.dispatch(searchActions.navigateToSearchResults());
        }, 200);
    }

    onResetMapClick = () => {
        if (this.props.fitBoundsToMarkers) {
            this.props.fitBoundsToMarkers({
                speed: 3,
                maxDuration: 3000,
            });
            this.props.resetVisibleMapItems(null);
        }
    };

    renderError() {
        const { err } = this.props.searchResults;
        if (err) {
            let msg = `Oops! We're having technical difficulties.
                    This is a temporary issue on our servers. Please try your search again.`;
            if (err.includes('Too Many Requests')) {
                msg =
                    'We are currently experiencing heavy traffic on our site. Please try again.';
            }
            return (
                <FlexCol xs={12}>
                    <Alert className={'mb-1'} type="error">
                        {msg}{' '}
                    </Alert>
                </FlexCol>
            );
        }
        return null;
    }

    renderSearchResults() {
        const siteTypes = this.getCampsiteTypeFilterValues();
        const isLoading = this.props.searchResults.loading;
        const searchSize = this.props.search.size || 20;
        const allResults = this.props.searchResults.results || [];

        // Filter results by visible map items BEFORE pagination
        const hasHiddenResults = Array.isArray(this.props.visibleMapItemIds);
        const filteredResults = hasHiddenResults
            ? allResults.filter((item) =>
                  this.props.visibleMapItemIds.includes(item.entity_id)
              )
            : allResults;

        // Use filtered pagination state when filtering, otherwise use Redux state
        const currentStart = hasHiddenResults
            ? this.state.filteredStart
            : this.props.search.start || 0;

        const { paginatedData } = paginator({
            items: filteredResults,
            activeIndex: currentStart / searchSize,
            perPage: searchSize,
        });

        const hasActivityTagsApplied =
            Object.keys(this.props.search.uiFilters?.activityTags || {}).length > 0;

        return (
            <FlexRow className="search-outer-wrap">
                {hasActivityTagsApplied && (
                    <FlexCol xs={12}>
                        <Alert className="mb-2" type="warning" hasAriaLive>
                            Please confirm desired activity is available at location
                            before booking.
                        </Alert>
                    </FlexCol>
                )}
                {this.renderError()}
                {paginatedData.map((item, index) => {
                    let highlightClass = '';

                    if (isLoading) {
                        return <CardLoading key={index} />;
                    }
                    if (
                        index === 0 &&
                        (this.props.search.entity_id === item.entity_id ||
                            this.props.search.what?.toLowerCase() ===
                                item.name?.toLowerCase())
                    ) {
                        highlightClass += ' search-result-highlight--success';
                    }

                    return (
                        <FlexCol xs={12} key={`card_${item.id}`}>
                            <CardSurfaceContext.Provider value="list">
                                <Card
                                    checkinTime={this.props.search.checkin_time}
                                    checkoutTime={this.props.search.checkout_time}
                                    dynamicClass={highlightClass}
                                    item={item}
                                    layout="campground"
                                    onCardOver={this.onCardOverEvent}
                                    onCardLeave={this.onCardLeaveEvent}
                                    siteTypes={
                                        item.entity_type === 'campground' ? siteTypes : []
                                    }
                                    val={highlightClass}
                                    screenWidth={this.props.screenWidth}
                                    isCampingSearch={this.props.isCampingSearch}
                                />
                            </CardSurfaceContext.Provider>
                        </FlexCol>
                    );
                })}
            </FlexRow>
        );
    }

    render() {
        let sortOptions;

        if (
            this.props.isCampingSearch ||
            (this.props.search.checkin_time && this.props.search.checkout_time)
        ) {
            sortOptions = SearchResultsList.sortOptionsAvailability;
        } else {
            sortOptions = SearchResultsList.sortOptionsDefault;
        }

        let currentSortSelection = this.props.search.sort;

        if (!currentSortSelection) {
            currentSortSelection = 'score';
        }

        const allResults = this.props.searchResults.results || [];
        const hasHiddenResults = Array.isArray(this.props.visibleMapItemIds);

        // Calculate total based on whether we're filtering by visible map items
        const filteredResults = hasHiddenResults
            ? allResults.filter((item) =>
                  this.props.visibleMapItemIds.includes(item.entity_id)
              )
            : allResults;
        const total = hasHiddenResults
            ? filteredResults.length
            : this.props.searchResults.total;
        const hiddenResultsCount = hasHiddenResults
            ? allResults.length - filteredResults.length
            : 0;

        // Use filtered pagination state when filtering, otherwise use Redux state
        const currentStart = hasHiddenResults
            ? this.state.filteredStart
            : this.props.search.start || 0;

        // The filters dialog opens over the results, while the desktop panel
        // replaces them
        const showResults =
            this.props.useNewSearchFiltersDialog || !this.props.search.filtersVisible;

        let searchResultsText = 'Search Results';
        if (this.props.searchResults.isBrowserLocation) {
            searchResultsText = 'Search Results Nearby';
        } else if (this.props.searchResults.placename) {
            searchResultsText = `Search Results near ${this.props.searchResults.placename}`;
        }

        return (
            <div
                ref={this.cardListDivRef}
                role="region"
                aria-label="Search Results List"
                className="search-left-rail-wrap"
            >
                <div
                    className={`${
                        this.props.search.filtersVisible ? 'search-filters-visible' : ''
                    }`}
                >
                    {this.props.screenWidth < 768 && (
                        <div className="header-filters-container header-primary">
                            <SearchCriteriaInputs
                                page={types.MAIN_SEARCH_RESULTS_PAGE}
                                onApplyFilters={this.onApplyFilters}
                                isCampingSearch={this.props.isCampingSearch}
                                flags={this.props.flags}
                                isFiltersDialogOpen={this.props.isFiltersDialogOpen}
                                onFiltersDialogOpenChange={
                                    this.props.onFiltersDialogOpenChange
                                }
                            />
                        </div>
                    )}

                    {this.props.useNewSearchFiltersDialog ? (
                        <SearchFiltersDialog
                            isOpen={!!this.props.isFiltersDialogOpen}
                            onOpenChange={this.props.onFiltersDialogOpenChange}
                            onApplyFilters={this.onApplyDialogFilters}
                        />
                    ) : (
                        this.props.search.filtersVisible && (
                            <SearchFilters
                                onApplyFilters={this.onApplyFilters}
                                searchAggregations={this.props.searchAggregations}
                                screenWidth={this.props.screenWidth}
                            />
                        )
                    )}

                    {showResults && (
                        <>
                            <div
                                className="search-left-rail-results"
                                id="search-results-list"
                            >
                                <FlexRow justifyContent="space-between">
                                    <FlexCol xs={12} sm={9}>
                                        <Heading headingLevel={1} appearance="h6">
                                            {decodeURI(searchResultsText || '')}
                                        </Heading>
                                    </FlexCol>
                                </FlexRow>
                                <AiSearchPrompt />
                                <FlexRow>
                                    <FlexCol>
                                        <SearchFilterPills flags={this.props.flags} />
                                    </FlexCol>
                                </FlexRow>

                                {total === 0 && !this.props.searchResults.loading ? (
                                    <NoSearchResultsMessage
                                        hasHiddenResults={hasHiddenResults}
                                        hiddenResultsCount={hiddenResultsCount}
                                        isResetMapDisabled={
                                            !this.props.fitBoundsToMarkers
                                        }
                                        onResetMapClick={this.onResetMapClick}
                                    />
                                ) : null}

                                <FlexRow
                                    hasGutters={false}
                                    className="search-total-filters"
                                >
                                    <FlexCol
                                        xs={5}
                                        xl={8}
                                        className="search-pagination-hide"
                                    >
                                        <SearchPaginationText
                                            start={currentStart}
                                            pageSize={this.props.search.size}
                                            total={total}
                                            isLoading={this.props.searchResults.loading}
                                            maxResults={searchActions.MAX_SEARCH_RESULTS}
                                        >
                                            {hasHiddenResults &&
                                                hiddenResultsCount > 0 && (
                                                    <span className="color-text-03">
                                                        {hiddenResultsCount} additional
                                                        {hiddenResultsCount === 1
                                                            ? ' result'
                                                            : ' results'}{' '}
                                                        off the map.{' '}
                                                        <button
                                                            type="button"
                                                            className="search--reset-map-zoom-button text-sm color-text-02"
                                                            onClick={this.onResetMapClick}
                                                            disabled={
                                                                !this.props
                                                                    .fitBoundsToMarkers
                                                            }
                                                        >
                                                            Recenter Map
                                                        </button>
                                                    </span>
                                                )}
                                        </SearchPaginationText>
                                    </FlexCol>

                                    {total > 0 && (
                                        <FlexCol
                                            xs={12}
                                            sm={7}
                                            xl={4}
                                            className={'search-sort-dropdown'}
                                        >
                                            <Select
                                                id={'search-sort-dropdown'}
                                                label="Sort By"
                                                isLabelVisible
                                                isInline
                                                options={sortOptions}
                                                onChange={(e) =>
                                                    this.handleSortChange(e.target.value)
                                                }
                                                value={currentSortSelection}
                                                gaTrackingId="189307795743"
                                            />
                                        </FlexCol>
                                    )}
                                </FlexRow>
                                {/* FlexRow for the search results defined above */}
                                {this.renderSearchResults()}

                                <A250SearchPromoWrapper />

                                <SearchPagination
                                    start={currentStart}
                                    pageSize={this.props.search.size}
                                    total={total}
                                    maxResults={searchActions.MAX_SEARCH_RESULTS}
                                    onPage={
                                        hasHiddenResults
                                            ? this.onFilteredPage
                                            : this.onPage
                                    }
                                />
                            </div>

                            <div className="results-footer-wrap">
                                <Footer inline />
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

function select(state) {
    return {
        search: state.search,
        searchResults: state.searchResults,
        searchAggregations: state.searchAggregations,
        visibleMapItemIds: state.searchMapSync?.visibleMapItemIds ?? null,
    };
}

const ConnectedSearchResultsList = connect(select)(SearchResultsList);

function SearchResultsListWithMapControl({
    clickedMapItemId,
    screenWidth,
    isCampingSearch,
    flags,
    isFiltersDialogOpen,
    onFiltersDialogOpenChange,
}) {
    const { fitBoundsToMarkers } = useMapControl();
    const dispatch = useDispatch();
    const { searchFilterModal } = useFlags();
    return (
        <ConnectedSearchResultsList
            clickedMapItemId={clickedMapItemId}
            screenWidth={screenWidth}
            isCampingSearch={isCampingSearch}
            flags={flags}
            fitBoundsToMarkers={fitBoundsToMarkers}
            resetVisibleMapItems={() => {
                dispatch(setVisibleMapItems(null));
            }}
            useNewSearchFiltersDialog={searchFilterModal}
            isFiltersDialogOpen={isFiltersDialogOpen}
            onFiltersDialogOpenChange={onFiltersDialogOpenChange}
        />
    );
}

SearchResultsListWithMapControl.propTypes = {
    clickedMapItemId: PropTypes.string,
    screenWidth: PropTypes.number,
    isCampingSearch: PropTypes.bool,
    flags: PropTypes.object,
    isFiltersDialogOpen: PropTypes.bool,
    onFiltersDialogOpenChange: PropTypes.func,
};

export default SearchResultsListWithMapControl;

// cSpell:ignore criterias, recarea, checkin, notreservable
